import React, { useState, useEffect } from 'react';
import { progressAPI } from '../services/api';
import RealtimeComparisonView from './RealtimeComparisonView';
import WeeklyComparisonView from './WeeklyComparisonView';
import styles from '../styles/BenchmarkCard.module.css';

/**
 * Benchmark Card Component
 * Smart wrapper that fetches benchmark data and displays appropriate view:
 * - Real-time cohort comparison for new users (Days 0-13)
 * - Weekly benchmarks for established users (Day 14+)
 *
 * Props:
 * - onError: Optional callback for error handling
 */
function BenchmarkCard({ onError }) {
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadBenchmarks = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const data = await progressAPI.getBenchmarks();
        setBenchmarkData(data);
      } catch (err) {
        console.error('[BenchmarkCard] Error loading benchmarks:', err);
        const errorMessage = err.message || 'Failed to load benchmark data';
        setError(errorMessage);

        if (onError) {
          onError(err);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadBenchmarks();
  }, [onError]);

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Loading your progress comparison...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <p className={styles.errorText}>Unable to load comparison data</p>
          <p className={styles.errorDetail}>{error}</p>
        </div>
      </div>
    );
  }

  // No data
  if (!benchmarkData) {
    return null;
  }

  const { benchmarkType, cohortComparison, stageFriendlyName, daysActive, weeklyComparison } = benchmarkData;

  // Render real-time comparison for new users (0-13 days)
  if (benchmarkType === 'realtime') {
    return (
      <RealtimeComparisonView
        cohortComparison={cohortComparison}
        stageFriendlyName={stageFriendlyName}
        daysActive={daysActive}
      />
    );
  }

  // Render weekly comparison for established users (14+ days)
  if (benchmarkType === 'weekly') {
    return (
      <WeeklyComparisonView
        weeklyComparison={weeklyComparison}
        daysActive={daysActive}
      />
    );
  }

  return null;
}

export default BenchmarkCard;
